/**
 * Support-login links. Proxied through our own server so the action sits behind
 * the same admin guard as every other action on this page; et-auth checks the
 * caller again on its side.
 */
const j = async (r: Response) => {
  if (r.ok) return r.json();
  let err: unknown;
  try {
    err = await r.json();
  } catch {
    err = { error: r.statusText };
  }
  throw err;
};

export type SupportLoginLink = {
  url: string;
  /** ISO timestamp — when the unredeemed link stops working. */
  expiresAt: string;
  /** Life of the session the link creates, once opened. */
  sessionTtlSeconds: number;
};

export const issueSupportLogin = (userId: number): Promise<SupportLoginLink> =>
  fetch("/api/internal/admin/support-login", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ userId }),
  }).then(j);

export const revokeSupportLogin = (
  grant: string,
): Promise<{ revoked: boolean }> =>
  fetch("/api/internal/admin/support-login/revoke", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ grant }),
  }).then(j);

/**
 * The secret rides in the URL *fragment* so it never reaches a server access
 * log; revoke needs it back out.
 */
export const grantFromUrl = (url: string): string | null => {
  try {
    const hash = new URL(url).hash.replace(/^#/, "");
    return new URLSearchParams(hash).get("grant");
  } catch {
    return null;
  }
};

/**
 * et-auth refusals arrive as `{ message }` (better-call APIError), while our
 * own proxy validation returns `{ error }`. `apiErrorMessage` from trials-api
 * reads `detail` and `error` only, so the actionable reason — "cannot create a
 * support login for an admin account" — was being replaced by the generic
 * fallback. Check all three.
 */
export const supportLoginErrorMessage = (
  e: unknown,
  fallback: string,
): string =>
  (e as { message?: string })?.message ??
  (e as { detail?: string })?.detail ??
  (e as { error?: string })?.error ??
  fallback;
